import { Link, Navigate } from "react-router-dom";
import { ArrowRight, CheckCircle, ChartLine, Bank, CalendarCheck, Coins, Briefcase, Sliders, PiggyBank, ListChecks } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/lib/api";
import site from "@/content/site.json";

const ICONS = { prices: ChartLine, pension: Bank, date: CalendarCheck, income: Coins, early: Briefcase, whatif: Sliders, ira: PiggyBank, checklist: ListChecks };
const H = site.home;

function Hero() {
  return (
    <section className="fade-up max-w-4xl" data-testid="home-hero">
      <p className="text-xs uppercase tracking-widest text-cyan-700 dark:text-cyan-400 font-semibold">{H.eyebrow}</p>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-grad-head mt-3 leading-tight" data-testid="home-title">{H.h1}</h1>
      <p className="text-base md:text-lg text-slate-600 dark:text-slate-400 mt-6 max-w-3xl">{H.intro}</p>
      <div className="flex flex-wrap gap-3 mt-8">
        <Link to="/login?tab=register"><Button size="lg" className="rounded-full" data-testid="home-signup-button">Create a free account<ArrowRight size={16} className="ml-1.5" /></Button></Link>
        <Link to="/learn"><Button size="lg" variant="outline" className="rounded-full" data-testid="home-guides-button">Read the guides</Button></Link>
        <Link to="/funds"><Button size="lg" variant="ghost" className="rounded-full" data-testid="home-prices-button">Today's TSP prices</Button></Link>
      </div>
    </section>
  );
}

function Tools() {
  return (
    <section className="mt-20" data-testid="home-tools">
      <h2 className="font-display text-2xl sm:text-3xl font-semibold">What you can do with PillarWealth</h2>
      <div className="grid md:grid-cols-2 gap-4 mt-8">
        {H.tools.map((t) => {
          const I = ICONS[t.key];
          return (
            <Link key={t.key} to={t.to} className="glass glass-lift p-6 block" data-testid={`home-tool-${t.key}`}>
              <I size={24} className="text-cyan-600" />
              <h3 className="font-display text-lg font-semibold mt-3">{t.title}</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-2 leading-relaxed">{t.body}</p>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

function Steps() {
  return (
    <section className="mt-20" data-testid="home-steps">
      <h2 className="font-display text-2xl sm:text-3xl font-semibold">How it works</h2>
      <ol className="grid md:grid-cols-3 gap-6 mt-8">
        {H.steps.map((s, i) => (
          <li key={s.title} className="border-l-2 border-cyan-500 pl-5">
            <p className="font-mono-num text-xs text-cyan-700 dark:text-cyan-400">STEP {i + 1}</p>
            <h3 className="font-display font-semibold mt-1">{s.title}</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">{s.body}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

function LatestGuides() {
  const { data } = useFetch("/articles?limit=40");
  const items = (data?.items || []).filter((a) => a.category !== "Daily TSP Market Recap").slice(0, 6);
  if (!items.length) return null;
  return (
    <section className="mt-20" data-testid="home-guides">
      <div className="flex items-end justify-between gap-4"><h2 className="font-display text-2xl sm:text-3xl font-semibold">Latest retirement guides</h2><Link to="/learn" className="text-sm text-cyan-700 dark:text-cyan-400 shrink-0">All guides →</Link></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
        {items.map((a) => (
          <Link key={a.slug} to={`/learn/${a.slug}`} className="glass glass-lift p-5 block" data-testid={`home-guide-${a.slug}`}>
            <p className="text-xs uppercase tracking-wider text-cyan-700 dark:text-cyan-400">{a.category}</p>
            <h3 className="font-display font-semibold mt-2 leading-snug">{a.title}</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-2 line-clamp-3">{a.excerpt}</p>
          </Link>
        ))}
      </div>
    </section>
  );
}

function Faq() {
  return (
    <section className="mt-20 max-w-3xl" data-testid="home-faq">
      <h2 className="font-display text-2xl sm:text-3xl font-semibold">Frequently asked questions</h2>
      <div className="mt-6 divide-y divide-border">
        {H.faq.map((f) => (
          <div key={f.q} className="py-5">
            <h3 className="font-semibold flex gap-2"><CheckCircle size={20} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />{f.q}</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-2 pl-7 leading-relaxed">{f.a}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const { user } = useAuth();
  if (user) return <Navigate to="/dashboard" replace />;
  const faqLd = { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: H.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) };
  return (
    <div data-testid="home-page">
      <Seo title={H.title} description={H.description} path="/" jsonLd={faqLd} />
      <Hero />
      <Tools />
      <Steps />
      <LatestGuides />
      <Faq />
    </div>
  );
}
